import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  FaProjectDiagram, 
  FaMapMarkerAlt, 
  FaCalendarAlt, 
  FaUsers, 
  FaMoneyBillWave,
  FaEdit,
  FaArrowLeft,
  FaChartLine,
  FaClipboardList,
  FaFileInvoiceDollar,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaArrowUp,
  FaArrowDown
} from 'react-icons/fa';
import IconWrapper from '../common/IconWrapper';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';

// Styled Components
const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  .header-left {
    display: flex;
    align-items: center;
    gap: 1rem;
    
    .back-button {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      background: none;
      border: none;
      color: #6c757d;
      cursor: pointer;
      padding: 0.5rem;
      border-radius: 8px;
      transition: all 0.2s ease;
      text-decoration: none;
      
      &:hover {
        color: #F68B21;
        background: rgba(246, 139, 33, 0.1);
      }
    }
  }
  
  h1 {
    font-size: 2rem;
    color: #141340;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 1rem;
    
    .icon {
      color: #F68B21;
    }
  }
`;

const EditButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(246, 139, 33, 0.3);
  }
`;

const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const ProjectCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(20, 19, 64, 0.05);
`;

const ProjectHeader = styled.div<{ status: string }>`
  background: ${props => {
    switch (props.status) {
      case 'In Progress': return 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)';
      case 'Completed': return 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)';
      case 'Draft': return 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)';
      default: return 'linear-gradient(135deg, #141340 0%, #2a2868 100%)';
    }
  }};
  padding: 2rem;
  color: white;
  position: relative;
  
  h2 {
    margin: 0;
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }
  
  .project-id {
    font-size: 1rem;
    opacity: 0.9;
    margin-bottom: 1rem;
  }
  
  .status-badge {
    position: absolute;
    top: 2rem;
    right: 2rem;
    background: rgba(255, 255, 255, 0.2);
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.85rem;
    font-weight: 600;
  }
`;

const ProjectInfo = styled.div`
  padding: 2rem;
  
  .info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
    margin-bottom: 2rem;
  }
  
  .info-item {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    background: #f8f9fa;
    border-radius: 8px;
    
    .icon {
      color: #F68B21;
      font-size: 1.2rem;
    }
    
    .content {
      .label {
        font-size: 0.85rem;
        color: #6c757d;
        margin-bottom: 0.25rem;
      }
      
      .value {
        font-weight: 600;
        color: #141340;
      }
    }
  }
`;

const ProgressSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(20, 19, 64, 0.05);
  
  h3 {
    margin: 0 0 1.5rem 0;
    color: #141340;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    .icon {
      color: #F68B21;
    }
  }
  
  .progress-bar {
    height: 12px;
    background: #f1f1f1;
    border-radius: 6px;
    overflow: hidden;
    margin-bottom: 1rem;
    
    .fill {
      height: 100%;
      background: linear-gradient(90deg, #F68B21 0%, #e67c1a 100%);
      border-radius: 6px;
      transition: width 0.3s ease;
    }
  }
  
  .progress-stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 1rem;
    
    .stat-item {
      text-align: center;
      padding: 1rem;
      background: #f8f9fa;
      border-radius: 8px;
      
      .value {
        font-size: 1.5rem;
        font-weight: 700;
        color: #F68B21;
        margin-bottom: 0.25rem;
      }
      
      .label {
        font-size: 0.85rem;
        color: #6c757d;
      }
    }
  }
`;

const BudgetSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(20, 19, 64, 0.05);
  
  h3 {
    margin: 0 0 1.5rem 0;
    color: #141340;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    .icon {
      color: #F68B21;
    }
  }
  
  .budget-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
  }
  
  .budget-item {
    padding: 1rem;
    background: #f8f9fa;
    border-radius: 8px;
    border-left: 4px solid #F68B21;
    
    .label {
      font-size: 0.85rem;
      color: #6c757d;
      margin-bottom: 0.25rem;
    }
    
    .amount {
      font-weight: 600;
      color: #141340;
    }
  }
  
  .budget-summary {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
    color: white;
    border-radius: 8px;
    
    .total-label {
      font-size: 0.9rem;
      opacity: 0.9;
    }
    
    .total-amount {
      font-size: 1.2rem;
      font-weight: 700;
    }
  }
`;

const SideSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const TeamSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(20, 19, 64, 0.05);
  
  h3 {
    margin: 0 0 1.5rem 0;
    color: #141340;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    .icon {
      color: #F68B21;
    }
  }
  
  .team-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  
  .team-member {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    background: #f8f9fa;
    border-radius: 8px;
    
    .avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: 600;
    }
    
    .member-info {
      flex: 1;
      
      .name {
        font-weight: 600;
        color: #141340;
        margin-bottom: 0.25rem;
      }
      
      .role {
        font-size: 0.85rem;
        color: #6c757d;
      }
    }
  }
`;

const RecentActivitySection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(20, 19, 64, 0.05);
  
  h3 {
    margin: 0 0 1.5rem 0;
    color: #141340;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    .icon {
      color: #F68B21;
    }
  }
  
  .activity-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  
  .activity-item {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem;
    background: #f8f9fa;
    border-radius: 8px;
    
    .icon {
      color: #F68B21;
      margin-top: 0.25rem;
    }
    
    .content {
      flex: 1;
      
      .message {
        color: #141340;
        margin-bottom: 0.25rem;
      }
      
      .time {
        font-size: 0.85rem;
        color: #6c757d;
      }
    }
  }
`;

const FinancialSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(20, 19, 64, 0.05);
  margin-bottom: 2rem;
  
  h3 {
    margin: 0 0 1.5rem 0;
    color: #141340;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .financial-summary {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 2rem;
    margin-bottom: 3rem;
    
    .summary-item {
      background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
      padding: 2rem;
      border-radius: 12px;
      text-align: center;
      box-shadow: 0 4px 20px rgba(20, 19, 64, 0.08);
      transition: transform 0.2s ease, box-shadow 0.2s ease;
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 30px rgba(20, 19, 64, 0.12);
      }
      
      &.income {
        border-left: 6px solid #28a745;
        border-top: 2px solid rgba(40, 167, 69, 0.2);
      }
      
      &.expenses {
        border-left: 6px solid #dc3545;
        border-top: 2px solid rgba(220, 53, 69, 0.2);
      }
      
      &.balance {
        border-left: 6px solid #F68B21;
        border-top: 2px solid rgba(246, 139, 33, 0.2);
      }
      
      .label {
        color: #6c757d;
        font-size: 1rem;
        font-weight: 500;
        margin-bottom: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      
      .amount {
        font-size: 2rem;
        font-weight: 700;
        
        &.positive {
          color: #28a745;
        }
        
        &.negative {
          color: #dc3545;
        }
        
        &.neutral {
          color: #F68B21;
        }
      }
    }
  }
  
  .financial-details {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
    
    .detail-section {
      h4 {
        color: #141340;
        margin: 0 0 1rem 0;
        font-size: 1.1rem;
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }
      
      .detail-list {
        max-height: 300px;
        overflow-y: auto;
        
        .detail-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem;
          border: 1px solid #e9ecef;
          border-radius: 6px;
          margin-bottom: 0.5rem;
          
          &:last-child {
            margin-bottom: 0;
          }
          
          .item-info {
            flex: 1;
            
            .description {
              color: #333;
              font-size: 0.9rem;
              margin-bottom: 0.25rem;
            }
            
            .date {
              color: #6c757d;
              font-size: 0.8rem;
            }
          }
          
          .amount {
            font-weight: 600;
            
            &.income {
              color: #28a745;
            }
            
            &.expense {
              color: #dc3545;
            }
          }
        }
        
        .no-data {
          text-align: center;
          color: #6c757d;
          padding: 2rem;
          font-style: italic;
        }
      }
    }
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #F68B21;
  font-size: 1.2rem;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: #dc3545;
  
  .icon {
    font-size: 4rem;
    margin-bottom: 1rem;
  }
  
  h3 {
    margin-bottom: 0.5rem;
  }
`;

const ManagerProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projectFinancials, setProjectFinancials] = useState<any>({
    income: [],
    expenses: [],
    totalIncome: 0,
    totalExpenses: 0,
    netBalance: 0
  });

  // Format functions
  const formatCurrency = (amount: number, currency: string = 'TZS') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
    return new globalThis.Date(dateString).toLocaleDateString('en-US', options);
  };

  // Get team members from project assigned staff (safe for null project)
  const teamMembers = project?.assignedStaff?.map((staff: any) => ({
    id: staff.userId._id || staff.userId.id || staff.userId,
    name: staff.userId.name || 'Unknown User',
    role: staff.role,
    avatar: (staff.userId.name || 'U').split(' ').map((n: string) => n[0]).join('').toUpperCase()
  })) || [];
  
  // Generate recent activities based on real data (safe for null project)
  const recentActivities = React.useMemo(() => {
    if (!project) return [];
    
    const activities = [];
    
    // Add project creation activity
    if (project.createdAt) {
      activities.push({
        id: 'project-created',
        message: `Project "${project.name}" was created`,
        time: formatDate(project.createdAt),
        icon: FaProjectDiagram
      });
    }
    
    // Add team assignment activities
    if (project.assignedStaff?.length > 0) {
      project.assignedStaff.forEach((staff: any, index: number) => {
        if (index < 2) { // Limit to first 2 to avoid clutter
          activities.push({
            id: `team-${staff.userId._id || staff.userId}`,
            message: `${staff.userId.name || 'Team member'} assigned as ${staff.role}`,
            time: formatDate(project.createdAt), // Using project creation date as assignment date
            icon: FaUsers
          });
        }
      });
    }
    
    // Add budget allocation activity
    if (project.budget?.total) {
      activities.push({
        id: 'budget-allocated',
        message: `Budget of ${formatCurrency(project.budget.total)} allocated`,
        time: formatDate(project.createdAt),
        icon: FaMoneyBillWave
      });
    }
    
    // Sort by most recent first and limit to 4 items
    return activities.slice(0, 4);
  }, [project]);
  
  useEffect(() => {
    if (!id) {
      setError('Project ID is required');
      setLoading(false);
      return;
    }
    
    // Fetch project from API
    const fetchProject = async () => {
      try {
        setLoading(true);
        
        const projectResponse = await apiService.getProject(id);
        const project = projectResponse.data;
        
        if (project) {
          // Check if user has access to this project (is manager via managerId or assignedStaff)
          const isDirectManager = project.managerId === user?.id || project.managerId?._id === user?.id;
          const isAssignedManager = project.assignedStaff?.some((staff: any) => 
            (staff.userId === user?.id || staff.userId?._id === user?.id) && staff.role === 'Project Manager'
          );
          
          if (isDirectManager || isAssignedManager) {
                          setProject(project);
              
              // Fetch project financial data
              await fetchProjectFinancials(project._id || project.id);
            } else {
              setError('You do not have access to this project');
            }
          } else {
            setError('Project not found');
          }
        } catch (error) {
          console.error('Error fetching project:', error);
          setError('Failed to load project');
        } finally {
          setLoading(false);
        }
      };

      fetchProject();
    }, [id, user]);

    // Function to fetch project financial data
    const fetchProjectFinancials = async (projectId: string) => {
      try {
        // Fetch accounting entries (all entries, will filter client-side if needed)
        const accountingResponse = await apiService.getAccountingEntries();
        const allAccountingEntries = accountingResponse.data || [];
        // Filter accounting entries for this project if they have projectId field
        const accountingEntries = allAccountingEntries.filter((entry: any) => 
          entry.projectId === projectId || entry.project === projectId
        );
        
        // Fetch expenses for this project
        const expensesResponse = await apiService.getExpenses({ projectId });
        const expenses = expensesResponse.data || [];
        
        // Fetch fund requests for this project
        const fundRequestsResponse = await apiService.getFundRequests({ projectId });
        const fundRequests = fundRequestsResponse.data || [];
        const approvedFundRequests = fundRequests.filter((req: any) => req.status === 'PAID');
        
        // Separate income and expenses from accounting entries
        const income = accountingEntries.filter((entry: any) => entry.type === 'Income' || entry.type === 'Revenue');
        const accountingExpenses = accountingEntries.filter((entry: any) => entry.type === 'Expense');
        
        // Calculate totals
        const totalIncome = income.reduce((sum: number, entry: any) => sum + (entry.amount || 0), 0);
        const totalAccountingExpenses = accountingExpenses.reduce((sum: number, entry: any) => sum + (entry.amount || 0), 0);
        const totalDirectExpenses = expenses.reduce((sum: number, expense: any) => sum + (expense.amount || 0), 0);
        const totalFundRequestExpenses = approvedFundRequests.reduce((sum: number, req: any) => sum + (req.amount || 0), 0);
        
        const totalExpenses = totalAccountingExpenses + totalDirectExpenses + totalFundRequestExpenses;
        const netBalance = totalIncome - totalExpenses;
        
        setProjectFinancials({
          income,
          expenses: [...accountingExpenses, ...expenses],
          fundRequestExpenses: approvedFundRequests,
          totalIncome,
          totalExpenses,
          netBalance
        });
      } catch (error) {
        console.error('Error fetching project financials:', error);
        // Don't set error state for financials, just log it
      }
    };
  
  if (loading) {
    return (
      <PageContainer>
        <LoadingSpinner>Loading project details...</LoadingSpinner>
      </PageContainer>
    );
  }
  
  if (error) {
    return (
      <PageContainer>
        <ErrorMessage>
          <IconWrapper icon={FaExclamationTriangle} className="icon" />
          <h3>Error</h3>
          <p>{error}</p>
        </ErrorMessage>
      </PageContainer>
    );
  }
  
  if (!project) {
    return (
      <PageContainer>
        <ErrorMessage>
          <IconWrapper icon={FaExclamationTriangle} className="icon" />
          <h3>Project Not Found</h3>
          <p>The requested project could not be found.</p>
        </ErrorMessage>
      </PageContainer>
    );
  }

  
  return (
    <PageContainer>
      <PageHeader>
        <div className="header-left">
          <button className="back-button" onClick={() => navigate('/manager/projects')}>
            <IconWrapper icon={FaArrowLeft} />
            Back to Projects
          </button>
          <h1>
            <IconWrapper icon={FaProjectDiagram} className="icon" />
            {project.name}
          </h1>
        </div>
      </PageHeader>
      
      <ProjectGrid>
        <MainSection>
          <ProjectCard>
            <ProjectHeader status={project.status}>
              <h2>{project.name}</h2>
              <div className="project-id">{project.id}</div>
              <div className="status-badge">{project.status}</div>
            </ProjectHeader>
            <ProjectInfo>
              <div className="info-grid">
                <div className="info-item">
                  <IconWrapper icon={FaMapMarkerAlt} className="icon" />
                  <div className="content">
                    <div className="label">Location</div>
                    <div className="value">{project.location}</div>
                  </div>
                </div>
                <div className="info-item">
                  <IconWrapper icon={FaCalendarAlt} className="icon" />
                  <div className="content">
                    <div className="label">Start Date</div>
                    <div className="value">{formatDate(project.startDate)}</div>
                  </div>
                </div>
                <div className="info-item">
                  <IconWrapper icon={FaCalendarAlt} className="icon" />
                  <div className="content">
                    <div className="label">End Date</div>
                    <div className="value">{formatDate(project.endDate)}</div>
                  </div>
                </div>
                <div className="info-item">
                  <IconWrapper icon={FaUsers} className="icon" />
                  <div className="content">
                    <div className="label">Team Size</div>
                    <div className="value">{project.assignedStaff?.length || 0} members</div>
                  </div>
                </div>
              </div>
            </ProjectInfo>
          </ProjectCard>
          
          <ProgressSection>
            <h3>
              <IconWrapper icon={FaChartLine} className="icon" />
              Project Progress
            </h3>
            <div className="progress-bar">
              <div className="fill" style={{ width: `${project.progress || 0}%` }}></div>
            </div>
            <div className="progress-stats">
              <div className="stat-item">
                <div className="value">{project.progress || 0}%</div>
                <div className="label">Completed</div>
              </div>
              <div className="stat-item">
                <div className="value">{100 - (project.progress || 0)}%</div>
                <div className="label">Remaining</div>
              </div>
              <div className="stat-item">
                <div className="value">{project.assignedStaff?.length || 0}</div>
                <div className="label">Team Members</div>
              </div>
            </div>
          </ProgressSection>
          
          <BudgetSection>
            <h3>
              <IconWrapper icon={FaMoneyBillWave} className="icon" />
              Budget Overview
            </h3>
            <div className="budget-grid">
              <div className="budget-item">
                <div className="label">Materials</div>
                <div className="amount">{formatCurrency(project.budget.materials)}</div>
              </div>
              <div className="budget-item">
                <div className="label">Labor</div>
                <div className="amount">{formatCurrency(project.budget.labor)}</div>
              </div>
              <div className="budget-item">
                <div className="label">Equipment</div>
                <div className="amount">{formatCurrency(project.budget.equipment)}</div>
              </div>
              <div className="budget-item">
                <div className="label">Sub-contracts</div>
                <div className="amount">{formatCurrency(project.budget.subContracts)}</div>
              </div>
              <div className="budget-item">
                <div className="label">Others</div>
                <div className="amount">{formatCurrency(project.budget.others)}</div>
              </div>
            </div>
            <div className="budget-summary">
              <div className="total-label">Total Budget</div>
              <div className="total-amount">{formatCurrency(project.budget.total)}</div>
            </div>
          </BudgetSection>
        </MainSection>
        
        <SideSection>
          <TeamSection>
            <h3>
              <IconWrapper icon={FaUsers} className="icon" />
              Team Members
            </h3>
            <div className="team-list">
              {teamMembers.map((member: any) => (
                <div key={member.id} className="team-member">
                  <div className="avatar">{member.avatar}</div>
                  <div className="member-info">
                    <div className="name">{member.name}</div>
                    <div className="role">{member.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </TeamSection>
          
          <RecentActivitySection>
            <h3>
              <IconWrapper icon={FaClipboardList} className="icon" />
              Recent Activity
            </h3>
            <div className="activity-list">
              {recentActivities.map(activity => (
                <div key={activity.id} className="activity-item">
                  <IconWrapper icon={activity.icon} className="icon" />
                  <div className="content">
                    <div className="message">{activity.message}</div>
                    <div className="time">{activity.time}</div>
                  </div>
                </div>
              ))}
            </div>
          </RecentActivitySection>
        </SideSection>
      </ProjectGrid>
      
      <FinancialSection>
        <h3>
          <IconWrapper icon={FaFileInvoiceDollar} className="icon" />
          Project Financials
        </h3>
        
        <div className="financial-summary">
          <div className="summary-item income">
            <div className="label">Total Income</div>
            <div className="amount positive">{formatCurrency(projectFinancials.totalIncome)}</div>
          </div>
          <div className="summary-item expenses">
            <div className="label">Total Expenses</div>
            <div className="amount negative">{formatCurrency(projectFinancials.totalExpenses)}</div>
          </div>
          <div className="summary-item balance">
            <div className="label">Net Balance</div>
            <div className={`amount ${projectFinancials.netBalance >= 0 ? 'positive' : 'negative'}`}>
              {formatCurrency(projectFinancials.netBalance)}
            </div>
          </div>
        </div>
        
        <div className="financial-details">
          <div className="detail-section">
            <h4>
              <IconWrapper icon={FaArrowUp} />
              Income Sources
            </h4>
            <div className="detail-list">
              {projectFinancials.income.length > 0 ? (
                projectFinancials.income.map((income: any, index: number) => (
                  <div key={index} className="detail-item">
                    <div className="item-info">
                      <div className="description">{income.description || income.category || 'Income Entry'}</div>
                      <div className="date">{formatDate(income.date || income.createdAt)}</div>
                    </div>
                    <div className="amount income">{formatCurrency(income.amount)}</div>
                  </div>
                ))
              ) : (
                <div className="no-data">No income recorded yet</div>
              )}
            </div>
          </div>
          
          <div className="detail-section">
            <h4>
              <IconWrapper icon={FaArrowDown} />
              Expense Details
            </h4>
            <div className="detail-list">
              {projectFinancials.expenses.length > 0 || projectFinancials.fundRequestExpenses?.length > 0 ? (
                <>
                  {projectFinancials.expenses.map((expense: any, index: number) => (
                    <div key={`expense-${index}`} className="detail-item">
                      <div className="item-info">
                        <div className="description">{expense.description || expense.category || 'Expense Entry'}</div>
                        <div className="date">{formatDate(expense.date || expense.createdAt)}</div>
                      </div>
                      <div className="amount expense">{formatCurrency(expense.amount)}</div>
                    </div>
                  ))}
                  {projectFinancials.fundRequestExpenses?.map((request: any, index: number) => (
                    <div key={`fund-${index}`} className="detail-item">
                      <div className="item-info">
                        <div className="description">Fund Request - {request.justification || 'Fund Request'}</div>
                        <div className="date">{formatDate(request.createdAt)}</div>
                      </div>
                      <div className="amount expense">{formatCurrency(request.amount)}</div>
                    </div>
                  ))}
                </>
              ) : (
                <div className="no-data">No expenses recorded yet</div>
              )}
            </div>
          </div>
        </div>
      </FinancialSection>
    </PageContainer>
  );
};

export default ManagerProjectDetail; 